import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

export type LatePolicySettingPayload = {
  id?: number
  graceTimeInMins: number
  deductionMethod: number
  lateCountPerDeduction: number
  isAnnualLeaveDeductable: boolean
  branchId: number
}

type AnyRecord = Record<string, unknown>

export type LatePolicySettingDto = {
  id?: number
  graceTimeInMins: number
  deductionMethod: number
  lateCountPerDeduction: number
  isAnnualLeaveDeductable: boolean
  branchId: number
}

export type DeductionMethodOption = {
  id: number
  name: string
  nameEn?: string
  nameAr?: string
}

function normalizeLatePolicy(row: any, fallbackBranchId: number): LatePolicySettingDto {
  return {
    id: Number(row?.id ?? row?.Id ?? row?.latePolicySettingId ?? row?.LatePolicySettingId ?? 0) || undefined,
    graceTimeInMins: Number(row?.graceTimeInMins ?? row?.GraceTimeInMins ?? row?.graceTime ?? row?.GraceTime ?? 0),
    deductionMethod: Number(row?.deductionMethod ?? row?.DeductionMethod ?? row?.deductionMethodId ?? row?.DeductionMethodId ?? 0),
    lateCountPerDeduction: Number(row?.lateCountPerDeduction ?? row?.LateCountPerDeduction ?? 0),
    isAnnualLeaveDeductable: Boolean(row?.isAnnualLeaveDeductable ?? row?.IsAnnualLeaveDeductable ?? true),
    branchId: Number(row?.branchId ?? row?.BranchId ?? fallbackBranchId ?? 0),
  }
}

function normalizeDeductionMethod(row: any): DeductionMethodOption {
  const id = Number(row?.id ?? row?.Id ?? row?.value ?? row?.Value ?? 0)
  const name =
    String(
      row?.name ??
        row?.Name ??
        row?.lookupNameEn ??
        row?.LookupNameEn ??
        row?.nameEn ??
        row?.NameEn ??
        row?.text ??
        row?.Text ??
        '',
    ).trim() || String(id)
  const nameEn = row?.nameEn ?? row?.NameEn ?? row?.lookupNameEn ?? row?.LookupNameEn
  const nameAr = row?.nameAr ?? row?.NameAr ?? row?.lookupNameAr ?? row?.LookupNameAr
  return { id, name, nameEn, nameAr }
}

/**
 * Late Policy service
 * Swagger: POST /api/LatePolicySetting/SaveOrUpdate (application/json-patch+json)
 */
class LatePolicyService {
  /** Some ERP endpoints return HTTP 200 with `{ result: { isError, msg } }`. */
  private assertResultOk(data: unknown, fallbackMsg: string): void {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return
    const o = data as AnyRecord
    const r = (o.result ?? o.Result) as unknown
    if (!r || typeof r !== 'object' || Array.isArray(r)) return
    const ro = r as AnyRecord
    if (ro.isError === true || ro.IsError === true) {
      const msgRaw = ro.msg ?? ro.Msg ?? ro.message ?? ro.Message
      const msg =
        typeof msgRaw === 'string' && msgRaw.trim() ? msgRaw.trim() : fallbackMsg
      const err = new Error(msg) as Error & { response?: { data: unknown } }
      err.response = { data }
      throw err
    }
  }

  async saveOrUpdate(payload: LatePolicySettingPayload): Promise<unknown> {
    const body: LatePolicySettingPayload = {
      id: payload.id != null ? Number(payload.id || 0) : undefined,
      graceTimeInMins: Number(payload.graceTimeInMins || 0),
      deductionMethod: Number(payload.deductionMethod || 0),
      lateCountPerDeduction: Number(payload.lateCountPerDeduction || 0),
      isAnnualLeaveDeductable: Boolean(payload.isAnnualLeaveDeductable),
      branchId: Number(payload.branchId || 0),
    }

    const response = await apiClient.post(endpoints.latePolicySetting.saveOrUpdate, body, {
      headers: { 'Content-Type': 'application/json-patch+json' },
    })

    const data = response.data
    this.assertResultOk(data, 'Late policy save/update failed.')
    return data
  }

  async getByBranchId(branchId: number): Promise<LatePolicySettingDto | null> {
    const bId = Number(branchId || 0)
    const response = await apiClient.get(endpoints.latePolicySetting.getByBranchId(bId))
    const payload = response.data

    // Most common shapes:
    // - direct object
    // - { data: {...} } / { Data: {...} }
    // - empty object / null when not configured
    const row =
      payload?.data ??
      payload?.Data ??
      payload?.result ??
      payload?.Result ??
      payload

    if (!row || typeof row !== 'object' || Array.isArray(row)) return null

    const dto = normalizeLatePolicy(row, bId)
    // If backend returns "empty" values but we still got an object, treat it as absent.
    const hasAnyValue =
      dto.graceTimeInMins !== 0 ||
      dto.deductionMethod !== 0 ||
      dto.lateCountPerDeduction !== 0 ||
      dto.isAnnualLeaveDeductable !== true

    return hasAnyValue ? dto : null
  }

  async getDeductionMethods(): Promise<DeductionMethodOption[]> {
    const response = await apiClient.get(endpoints.latePolicySetting.getDeductionMethods)
    const payload = response.data

    const list: any[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.Data)
          ? payload.Data
          : []

    return list.map(normalizeDeductionMethod).filter((x) => Number.isFinite(x.id) && x.id >= 0)
  }
}

export const latePolicyService = new LatePolicyService()

